import React from 'react'
import { useQuery } from '@apollo/client'
import {
  TestSessionsWithoutMembersVariables,
  TestSessionsWithoutMembers,
  SortDirectionEnum,
  TestSessionStatus,
  Lab,
} from '../../types'
import { SortByType } from '../../constants'
import { TEST_SESSIONS } from './operations'

interface TestSessionsFilter {
  statuses?: TestSessionStatus[]
  dateFrom?: string
  dateTo?: string
  userId?: string
  groupId?: string
  groupIds?: string[]
  companyIds?: string[]
  companyId?: string
  companyOrGroup?: string
  userNameOrEmail?: string
  isParticipant?: boolean
  isStaff?: boolean
  lab?: Lab
  userIds?: string[]
}

export type Sorter = {
  sortBy?: string
  direction?: SortDirectionEnum
}

interface Props {
  filter?: TestSessionsFilter
  sorter?: Sorter
  page?: number
  pageLength?: number
  skip?: boolean
}

const PAGE_LENGTH = 10

export const useTestSessionsWithoutMembersData = ({
  filter,
  sorter,
  page = 1,
  pageLength = PAGE_LENGTH,
  skip = false,
}: Props) => {
  const {
    userId,
    companyIds,
    companyId,
    groupId,
    groupIds,
    companyOrGroup,
    userNameOrEmail,
    dateFrom,
    dateTo,
    statuses,
    isParticipant,
    isStaff,
    lab,
    userIds,
  } = filter || {}
  const queryVariables = React.useMemo<TestSessionsWithoutMembersVariables>(
    () => ({
      filterData: {
        dateBetween: [dateFrom, dateTo],
        statuses,
        companyIds,
        groupIds,
        lab,
        company:
          companyOrGroup || companyId
            ? {
                nameOrGroupNameLike: companyOrGroup,
                id: companyId,
              }
            : null,
        group: groupId ? { id: groupId } : null,
        user:
          userNameOrEmail || userId || isParticipant !== undefined || isStaff !== undefined
            ? {
                id: userId,
                emailOrNameLike: userNameOrEmail,
                isParticipant,
                isStaff,
              }
            : null,
      },
      groupsInputData: {
        groupIds,
        memberUserIds: userIds,
      },
      companyInputData: {
        memberUserIds: userIds,
      },
      pagination: {
        from: pageLength * (page - 1),
        length: pageLength,
        sortBy: sorter?.sortBy || SortByType.DATE,
        direction: sorter?.direction || SortDirectionEnum.DESC,
      },
      gmt: new Date().getTimezoneOffset(),
    }),
    [
      dateFrom,
      dateTo,
      statuses,
      companyIds,
      groupIds,
      companyOrGroup,
      companyId,
      groupId,
      userNameOrEmail,
      userId,
      isParticipant,
      isStaff,
      page,
      pageLength,
      sorter?.sortBy,
      sorter?.direction,
      lab,
      userIds,
    ]
  )
  const testSessionsQuery = useQuery<
    TestSessionsWithoutMembers,
    TestSessionsWithoutMembersVariables
  >(TEST_SESSIONS, {
    variables: queryVariables,
    skip,
  })

  return testSessionsQuery
}
